/**
 * Stone Investment Company Story
 * 
 * Two-column story section with Stone Investment's luxury aesthetic:
 * - Editorial layout
 * - Image + text pairing
 * - Stats integration
 */

'use client';

import Image from 'next/image';
import type { CompanyStoryProps } from '@routeroyal/theme-core';

/**
 * StoneCompanyStory - Editorial company story section
 */
export function StoneCompanyStory({
  title,
  content,
  image,
  imagePosition = 'right',
  stats,
  background = 'white'
}: CompanyStoryProps) {
  const contentArray = Array.isArray(content) ? content : [content];
  const bgClass = background === 'gray' ? 'bg-stone-100' : 'bg-white';
  
  return (
    <section className={`py-20 lg:py-32 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
          imagePosition === 'left' ? 'lg:flex-row-reverse' : ''
        }`}>
          {/* Content Column */}
          <div className={imagePosition === 'left' ? 'lg:order-2' : ''}>
            {/* Section Title */}
            <h2 
              className="font-serif text-4xl lg:text-5xl text-stone-900 mb-8 leading-[1.1]"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title}
            </h2>
            
            {/* Content Paragraphs */}
            <div className="space-y-6">
              {contentArray.map((paragraph, index) => (
                <p 
                  key={index}
                  className="font-sans text-stone-600 text-base lg:text-lg leading-relaxed"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {paragraph}
                </p>
              ))}
            </div>
            
            {/* Stats */}
            {stats && stats.length > 0 && (
              <div className="flex flex-wrap gap-8 lg:gap-12 mt-12 pt-8 border-t border-stone-200">
                {stats.map((stat, index) => (
                  <div key={index}>
                    <span 
                      className="font-serif text-4xl lg:text-5xl text-stone-900 block"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      {stat.value}
                    </span>
                    <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-1 block">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Image Column */}
          {image && (
            <div className={`relative ${imagePosition === 'left' ? 'lg:order-1' : ''}`}>
              <div className="relative aspect-[4/5] overflow-hidden bg-stone-200">
                <Image
                  src={image}
                  alt={title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
              
              {/* Decorative Element */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-stone-300 -z-10 hidden lg:block" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default StoneCompanyStory;
